import React, { FC, useEffect } from 'react';
import { gql, useQuery } from '@apollo/client';
import { useDispatch, useI18n, useLogger, useSelector } from '~Hooks';
import { GetLayouts, GetLayoutsVariables } from './__queries__';

import HandySettingsBlock, { SettingBlockItemType } from '../../../Components/HandySettingsBlock';
import { useStaffContext } from '../../../Context/StaffProvider';
import { useStorage } from '~Components/Storage';
import { Box } from '~Styles';

export const GET_LAYOUTS = gql`
  query GetLayouts($venueId: ID!) {
    venue: Venue(id: $venueId) {
      id
      company {
        id
        layouts {
          id
          name {
            id
            se
            en
            es
            no
          }
        }
      }
    }
  }
`;

const POSLayoutSelection: FC = () => {
  const logger = useLogger('handy');
  const venueId = useSelector((state) => state.venueId);
  const { i18n } = useI18n();
  const dispatch = useDispatch();

  const demoMode = useSelector((state) => state.demoMode);
  let selectedLayoutId = useSelector((state) => state.POSLayout);

  if (demoMode) {
    selectedLayoutId = window.DEMO_MODE_LAYOUT_ID;
  }

  const { authedStaff } = useStaffContext();
  const [storage, updateStorage] = useStorage();

  const { data } = useQuery<GetLayouts, GetLayoutsVariables>(GET_LAYOUTS, {
    variables: { venueId: venueId || '' },
    skip: !venueId,
    fetchPolicy: 'cache-and-network',
  });

  const layouts = data?.venue?.company?.layouts || [];

  const updatePOSLayout = (layout: string) => {
    logger.info('Updated POS layout', {
      staff: authedStaff,
      fromLayout: selectedLayoutId,
      toLayout: layout,
    });

    dispatch({
      type: 'SET_POS_LAYOUT',
      POSLayout: layout,
    });
    window.postMessageBridge({
      devicePOSLayout: layout,
    });
  };

  // Store layouts on first load
  useEffect(() => {
    if (!data?.venue) {
      return;
    }

    const POSLayoutIds = layouts.map((layout) => layout.id);
    dispatch({
      type: 'SET_POS_LAYOUTS',
      POSLayouts: POSLayoutIds,
    });
    window.postMessageBridge({
      POSLayouts: POSLayoutIds,
    });
  }, [data?.venue]);

  // Layout selection settings
  const layoutSettings: SettingBlockItemType[] = [
    {
      id: 'none',
      title: `${i18n('Settings.LayoutNone')} ${i18n('Settings.LayoutUseCategories')} ${
        selectedLayoutId === 'NONE' ? `(${i18n('Handy.Active')})` : ''
      }`,
      action: {
        onClick: () => updatePOSLayout('NONE'),
      },
    },
    ...layouts.map((layout) => ({
      id: layout.id,
      title: `${i18n(layout.name)} ${
        selectedLayoutId === layout.id ? `(${i18n('Handy.Active')})` : ''
      }`,
      action: {
        onClick: () => updatePOSLayout(layout.id),
      },
    })),
  ];

  // Toggle settings for POS layout appearance
  const appearanceSettings: SettingBlockItemType[] = [
    {
      id: 'show-prices-on-buttons',
      title: i18n('Settings.PosLayout.ShowPrices'),
      action: {
        onClick: () => {
          logger.info('Toggling showPricesOnButtons', {
            from: storage.showPricesOnButtons,
            to: !storage.showPricesOnButtons,
          });
          updateStorage({
            showPricesOnButtons: !storage.showPricesOnButtons,
          });
        },
        isToggle: true,
        selected: storage.showPricesOnButtons,
      },
    },
    {
      id: 'require-beeper-number',
      title: i18n('Settings.PosLayout.RequireBeeper'),
      additionalText: {
        left: i18n('Settings.PosLayout.RequireBeeper.Desc'),
      },
      action: {
        onClick: () => {
          logger.info('Toggling requireBeeperNumber', {
            from: storage.requireBeeperNumber,
            to: !storage.requireBeeperNumber,
          });
          updateStorage({
            requireBeeperNumber: !storage.requireBeeperNumber,
          });
        },
        isToggle: true,
        selected: storage.requireBeeperNumber,
      },
    },
  ];

  return (
    <>
      <HandySettingsBlock items={layoutSettings} />
      <Box marginTop="large">
        <HandySettingsBlock
          title={i18n('Settings.PosLayout.Settings')}
          items={appearanceSettings}
        />
      </Box>
    </>
  );
};

export default POSLayoutSelection;
